import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useUserTenantAccess } from 'hooks/api';
import type { TenantUserResponse, UserTenantAccessRecord } from 'api/types';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ============================================================================
// TYPES
// ============================================================================

interface TenantUserViewModalProps {
  isOpen: boolean;
  user: TenantUserResponse | null;
  tenantName?: string;
  onClose: () => void;
  onEdit: (user: TenantUserResponse) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (userName: string) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
    <div style={{ width: '180px', flexShrink: 0, fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
      {label}
    </div>
    <div style={{
      flex: 1,
      fontSize: '0.85rem',
      color: 'var(--text-primary)',
      background: 'var(--bg-secondary)',
      border: '1px solid var(--form-border-color)',
      borderRadius: '0.375rem',
      padding: '0.375rem 0.625rem',
      minHeight: '2rem',
      display: 'flex',
      alignItems: 'center',
    }}>
      {value || '-'}
    </div>
  </div>
);

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0.75rem',
    background: 'var(--bg-secondary)',
    borderLeft: '3px solid var(--primary)',
    borderRadius: '0 0.25rem 0.25rem 0',
    marginBottom: '0.75rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  }}>
    {icon}
    {title}
  </div>
);

const getPrivilegeBadge = (level: string) => {
  switch (level?.toLowerCase()) {
    case 'super_admin': return <span className="badge badge-danger">Super Admin</span>;
    case 'admin': return <span className="badge badge-warning">Admin</span>;
    case 'support': return <span className="badge badge-primary">Support</span>;
    case 'user': return <span className="badge badge-info">User</span>;
    default: return <span className="badge badge-default">{level || '-'}</span>;
  }
};

const getStatusBadge = (statusCode?: string) => {
  const code = (statusCode || '').toLowerCase();
  if (code.includes('active')) return <span className="badge badge-active">Active</span>;
  if (code.includes('inactive') || code.includes('disabled')) return <span className="badge badge-inactive">Inactive</span>;
  if (code.includes('suspended')) return <span className="badge badge-suspended">Suspended</span>;
  return <span className="badge badge-default">{statusCode || '-'}</span>;
};

// ---- Tenant Access Table ----
const TenantAccessTable: React.FC<{ records: UserTenantAccessRecord[]; loading: boolean; currentTenantId?: string }> = ({ records, loading, currentTenantId }) => {
  if (loading) {
    return <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>Loading tenant access...</div>;
  }
  if (!records.length) {
    return <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>No tenant access records found.</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
            <th style={{ textAlign: 'left', padding: '0.375rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Tenant</th>
            <th style={{ textAlign: 'left', padding: '0.375rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Privilege</th>
            <th style={{ textAlign: 'center', padding: '0.375rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Primary</th>
            <th style={{ textAlign: 'left', padding: '0.375rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {records.map((rec) => {
            const isCurrent = rec.tenantId === currentTenantId;
            return (
              <tr key={rec.id} style={{
                borderBottom: '1px solid var(--border-color)',
                background: isCurrent ? 'var(--info-bg, rgba(59, 130, 246, 0.05))' : undefined,
              }}>
                <td style={{ padding: '0.375rem 0.5rem', color: 'var(--text-primary)' }}>
                  {rec.tenant ? `${rec.tenant.code} — ${rec.tenant.name}` : rec.tenantId.slice(0, 8)}
                  {isCurrent && <span style={{ marginLeft: '0.375rem', fontSize: '0.7rem', color: 'var(--primary)', fontWeight: 600 }}>(current)</span>}
                </td>
                <td style={{ padding: '0.375rem 0.5rem' }}>{getPrivilegeBadge(rec.privilegeLevel)}</td>
                <td style={{ padding: '0.375rem 0.5rem', textAlign: 'center' }}>
                  <span className={`badge ${rec.isPrimaryTenant ? 'badge-active' : 'badge-default'}`} style={{ fontSize: '0.7rem' }}>
                    {rec.isPrimaryTenant ? 'Yes' : 'No'}
                  </span>
                </td>
                <td style={{ padding: '0.375rem 0.5rem' }}>{getStatusBadge(rec.statusCode)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// COMPONENT
// ============================================================================

const TenantUserViewModal: React.FC<TenantUserViewModalProps> = ({
  isOpen,
  user,
  tenantName = '',
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  // Hooks must be called unconditionally (before any early return)
  const { data: tenantAccess, loading: loadingAccess } = useUserTenantAccess(
    isOpen && user ? user.userId : undefined
  );

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !user) return;
    minimize({
      id: `${tabId}-view-modal-${user.userName}`,
      title: `View - ${user.userName}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(user.userName),
    });
    onClose();
  }, [tabId, moduleName, user, minimize, onClose, onRestoreView]);

  if (!isOpen || !user) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.user size={20} />
            User Details
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {/* Record Info */}
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.tag size={16} />} title="Record Info" />
            {tenantName && <DetailRow label="Tenant" value={tenantName} />}
            <DetailRow label="User ID" value={
              <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{user.id}</span>
            } />
            <DetailRow label="Created Date" value={new Date(user.createdAt).toLocaleString()} />
          </div>

          {/* Identity Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.info size={16} />} title="Identity" />
            <DetailRow label="Username" value={user.userName} />
            <DetailRow label="First Name" value={user.firstName} />
            {user.middleName && <DetailRow label="Middle Name" value={user.middleName} />}
            <DetailRow label="Last Name" value={user.lastName} />
            <DetailRow label="Email" value={user.email} />
            <DetailRow label="Privilege Level" value={getPrivilegeBadge(user.privilegeLevel)} />
            <DetailRow label="Status" value={getStatusBadge(user.statusCode)} />
          </div>

          {/* Tenant Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.home size={16} />} title="Tenant" />
            <DetailRow label="Primary Tenant" value={
              <span className={`badge ${user.isPrimaryTenant ? 'badge-active' : 'badge-default'}`}>
                {user.isPrimaryTenant ? 'Yes' : 'No'}
              </span>
            } />
            <DetailRow label="Last Activity" value={user.lastActivityAt ? new Date(user.lastActivityAt).toLocaleString() : undefined} />
          </div>

          {/* Tenant Access Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.users size={16} />} title="Tenant Access" />
            <TenantAccessTable records={tenantAccess} loading={loadingAccess} currentTenantId={user.tenantId} />
          </div>

          {/* Dates Section */}
          <div>
            <SectionHeader icon={<IconSystem.calendar size={16} />} title="Dates" />
            <DetailRow label="Updated" value={new Date(user.updatedAt).toLocaleString()} />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-primary" onClick={() => onEdit(user)}>
            <IconSystem.edit size={16} style={{ marginRight: '0.25rem' }} />
            Edit
          </button>
        </div>
      </div>
    </>
  );
};

export default TenantUserViewModal;
